import { parseReportMarkdown, type InlineRun } from '@/lib/reportMarkdown';
import { cn } from '@/lib/utils';

const Runs = ({ runs }: { runs: InlineRun[] }) => (
  <>
    {runs.map((r, i) =>
      r.bold ? <strong key={i} className="font-semibold text-foreground">{r.text}</strong> : <span key={i}>{r.text}</span>,
    )}
  </>
);

export const ReportContent = ({ content, className }: { content: string; className?: string }) => {
  const blocks = parseReportMarkdown(content);
  return (
    <div className={cn('space-y-3 text-sm leading-relaxed text-foreground/85', className)}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading':
            return b.level === 1 ? (
              <h2 key={i} className="text-xl font-semibold text-foreground tracking-tight pt-1">
                <Runs runs={b.runs} />
              </h2>
            ) : (
              <h3
                key={i}
                className={cn(
                  'font-semibold text-foreground pt-3',
                  b.level === 2 ? 'text-base border-b border-border pb-1.5' : 'text-sm',
                )}
              >
                <Runs runs={b.runs} />
              </h3>
            );
          case 'paragraph':
            return <p key={i}><Runs runs={b.runs} /></p>;
          case 'rule':
            return <hr key={i} className="border-border" />;
          case 'list': {
            const Tag = b.ordered ? 'ol' : 'ul';
            return (
              <Tag key={i} className={cn('ps-5 space-y-1', b.ordered ? 'list-decimal' : 'list-disc marker:text-primary/60')}>
                {b.items.map((it, j) => <li key={j}><Runs runs={it} /></li>)}
              </Tag>
            );
          }
          case 'table':
            return (
              <div key={i} className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-start">
                    <tr>
                      {b.header.map((h, j) => (
                        <th key={j} className="px-3 py-2 text-start font-semibold text-foreground whitespace-nowrap">
                          <Runs runs={h} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r} className="border-t border-border">
                        {row.map((cell, c) => (
                          <td key={c} className="px-3 py-2 align-top"><Runs runs={cell} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
        }
      })}
    </div>
  );
};

export default ReportContent;
